import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = 'duel:tutorials_seen';

export interface TutorialStep {
  text: string;
  /** Emoji or icon hint pointing to the relevant UI area */
  hint?: string;
}

export interface TutorialContent {
  title: string;
  steps: TutorialStep[];
}

export const TUTORIALS: Record<string, TutorialContent> = {
  home: {
    title: 'Welcome to DUEL',
    steps: [
      { text: 'Your Strength Score is at the top — it updates every time you log a Core 5 lift. The higher it climbs, the higher you rank.', hint: '☝️ Score card above' },
      { text: 'Tap the Record tab (bottom) to log a workout. Log your Bench, Squat, Deadlift, Pull-Up, and Dead Hang to get fully ranked.', hint: '👇 Record tab at the bottom' },
      { text: 'The Duels tab lets you challenge anyone at your gym. Your lifts are verified by a community judge panel.', hint: '⚔️ Duels tab at the bottom' },
    ],
  },
  record: {
    title: 'Logging Workouts',
    steps: [
      { text: 'Tap an exercise name to search or browse by muscle group. Your most recent exercises appear first.', hint: '☝️ Exercise name above' },
      { text: 'Enter weight and reps for each set. Tap "+ Add Set" to add more. The confirm button (✓) logs the set and starts your rest timer.', hint: '➡️ Set rows above' },
      { text: 'Tap "Advanced" on any exercise to add RPE, notes, and video proof for ranked lifts.', hint: '☝️ Advanced toggle' },
    ],
  },
  rank: {
    title: 'Leaderboard',
    steps: [
      { text: 'You are ranked against lifters at your gym and globally by Strength Score. Complete your Core 5 lifts to appear on the board.', hint: '☝️ Rankings above' },
      { text: 'Tap "Filters" to sort by Win Rate, Strength Score, or Volume. Switch between your gym, global, and gym battle views.', hint: '☝️ Filters toggle' },
    ],
  },
  duels: {
    title: 'How Duels Work',
    steps: [
      { text: 'Tap "New Duel" to challenge someone. Pick an exercise, set the time limit, and send the challenge. They have 7 days to accept.', hint: '☝️ New Duel button' },
      { text: 'Both lifters log their best efforts during the duel window. Higher total volume wins. Video proof is required for ranked duels.', hint: '📹 Submit your lifts' },
      { text: 'Results are judged by a 3-person panel. 2/3 white lights = good lift. Contested results go to a head judge.', hint: '⚖️ Judge panel' },
    ],
  },
  profile: {
    title: 'Your Profile',
    steps: [
      { text: 'Your Core 5 PRs are shown here — these drive your Strength Score. Keep them fresh (log every 4 weeks minimum to avoid score decay).', hint: '☝️ PR section above' },
      { text: 'The heatmap shows your training consistency. Green = trained that day. Gaps cost you score freshness.', hint: '📅 Heatmap below' },
      { text: 'Tap the Compare button on any public profile to see a side-by-side strength comparison. Challenge directly from there.', hint: '⚔️ Challenge button' },
    ],
  },

  // ── Tier upgrade tutorials ──
  pro_upgrade: {
    title: 'Welcome to Pro',
    steps: [
      { text: 'Score History — track your strength progression over time' },
      { text: 'Advanced Analytics — volume trends, PR velocity, training splits' },
      { text: 'Workout Templates — create custom programs or use our built-in templates' },
      { text: 'Data Export — download your complete workout history as CSV' },
    ],
  },
  coach_upgrade: {
    title: 'Coach Tools Unlocked',
    steps: [
      { text: 'Client Dashboard — see all your athletes at a glance' },
      { text: 'Program Builder — create multi-day training programs and assign them' },
      { text: 'Coach Notes — track form cues, injuries, and progress per client' },
      { text: 'Adherence Tracking — know who\'s showing up and who needs a nudge' },
      { text: 'Invite clients with your coach link from Settings' },
    ],
  },
  club_upgrade: {
    title: 'Club Tools Unlocked',
    steps: [
      { text: 'TV Mode — display a live leaderboard on your gym\'s screens' },
      { text: 'Gym Challenges — run monthly competitions for your members' },
      { text: 'Gym Battle — see how your gym ranks against others in your area' },
      { text: 'QR Join Code — print it at the front desk so members can join instantly' },
      { text: 'Member Analytics — spot who\'s at risk of dropping off before they do' },
    ],
  },
};

async function getSeenSet(): Promise<Set<string>> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {}
  return new Set();
}

export async function hasSeen(screenKey: string): Promise<boolean> {
  const seen = await getSeenSet();
  return seen.has(screenKey);
}

export async function markSeen(screenKey: string): Promise<void> {
  const seen = await getSeenSet();
  seen.add(screenKey);
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
}

export async function resetAll(): Promise<void> {
  await AsyncStorage.removeItem(STORAGE_KEY);
}
